/**
 * Copyright (c) 2023 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 */

import { useEffect, useState } from 'react';
import { useBestFitQuery } from './useBestFitQuery';

/**
 * Custom useLazyBestFitQuery hook
 * Fetch data in a lazy way with react query. Keep in mind, that the query is not execute again,
 * if the queryParameters are not changed.
 *
 *  Example Usage: (No useEffect needed!!!)
 *  export function MyComponent() {
 *  ...
 *  const mySuccessFunction = (data) => {
 *     doSomethingWithResponseData(data);
 *  };
 *  const myErrorFunction = (error) => {
 *     doSomethingWithTheError(error);
 *  };
 *  const { lazyFetch, isLoading } = useLazyQuery('myApiEndpointKey', mySuccessFunction, myErrorFunction);
 *  ...
 *  const myFetchFunction = async (queryData, myUrlPathSegment) => {
 *     await lazyFetch(queryData, [myUrlPathSegment]);
 *   };
 *   ...
 * };
 *
 * @param apiEndpointKey
 * @param reactQueryOptions
 * @param axiosOptions
 * @returns {{isLoading: boolean, lazyFetch: (function(*, null=): Promise<axios.AxiosResponse<any>>)}}
 */
export const useLazyBestFitQuery = (
  apiEndpointKey,
  reactQueryOptions = {},
  axiosOptions = {},
) => {
  const [queryVariables, setQueryVariables] = useState(null);

  const queryDefaultOptions = {
    refetchOnWindowFocus: false,
    enabled: Boolean(queryVariables),
    retry: 0,
    staleTime: 0,
    cacheTime: 0,
  };

  const queryInfo = useBestFitQuery(
    apiEndpointKey,
    queryVariables,
    null,
    { ...queryDefaultOptions, ...reactQueryOptions },
    axiosOptions,
  );

  useEffect(() => {
    if (queryInfo.status === 'success' || queryInfo.status === 'error') {
      setQueryVariables(null);
    }
  }, [queryInfo.status]);

  return {
    lazyQuery: setQueryVariables,
    ...queryInfo,
  };
};
